// src/pages/Production/CostCalculationDetail.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, onSnapshot, query, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import CostBreakdown from '../../components/production/CostBreakdown';
import { calculateDetailedCost } from '../../utils/costCalculationUtils';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    backLink: {
        textDecoration: 'none',
        color: '#06C167',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '30px'
    },
    stageContainer: {
        background: '#f8fafc',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        border: '1px solid #e2e8f0'
    },
    stageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px'
    },
    stageTitle: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#0f172a',
        margin: 0
    },
    stageBadge: {
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '500'
    },
    materialItem: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '10px 0',
        borderBottom: '1px solid #e2e8f0'
    },
    outputProduct: {
        background: '#dcfce7',
        padding: '10px 15px',
        borderRadius: '8px',
        marginTop: '15px'
    },
    subcontractorInfo: {
        background: '#dbeafe',
        padding: '10px 15px',
        borderRadius: '8px',
        marginTop: '10px'
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    noRecipeContainer: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    purchaseInfoBadge: {
        background: '#dcfce7',
        color: '#166534',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '10px',
        fontWeight: '500',
        marginLeft: '8px'
    }
};

function CostCalculationDetail() {
    const { productId } = useParams();
    const { userData } = useAuth();
    const [product, setProduct] = useState(null);
    const [recipe, setRecipe] = useState(null);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [subcontractors, setSubcontractors] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [rawMaterialPurchases, setRawMaterialPurchases] = useState([]); // New: purchases data
    const [costData, setCostData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Fetch real data from Firebase
    useEffect(() => {
        if (!userData?.companyId || !productId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        // Fetch the specific product
        const fetchProduct = async () => {
            const productDoc = await getDoc(doc(db, 'companies', companyId, 'products', productId));
            if (productDoc.exists()) {
                setProduct({ id: productDoc.id, ...productDoc.data() });
            }
        };
        fetchProduct();

        // Fetch all products (for raw materials)
        const unsubProducts = onSnapshot(
            query(collection(db, 'companies', companyId, 'products')),
            snap => setAllProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch recipes
        const unsubRecipes = onSnapshot(
            query(collection(db, 'companies', companyId, 'recipes')),
            snap => {
                const recipes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                // Find the recipe for this product
                const productRecipe = recipes.find(r =>
                    r.productId === productId ||
                    r.product === product?.name
                );
                setRecipe(productRecipe || null);
            }
        );

        // Fetch raw materials
        const unsubRawMaterials = onSnapshot(
            query(collection(db, 'companies', companyId, 'rawMaterials')),
            snap => setRawMaterials(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // Fetch subcontractors (suppliers)
        const unsubSubcontractors = onSnapshot(
            query(collection(db, 'companies', companyId, 'suppliers')),
            snap => setSubcontractors(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        // NEW: Fetch raw material purchases
        const unsubPurchases = onSnapshot(
            query(collection(db, 'companies', companyId, 'rawMaterialPurchases')),
            snap => {
                setRawMaterialPurchases(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                setLoading(false);
            }
        );

        return () => {
            unsubProducts();
            unsubRecipes();
            unsubRawMaterials();
            unsubSubcontractors();
            unsubPurchases();
        };
    }, [userData, productId]);

    // Update recipe when product name is available
    useEffect(() => {
        if (!userData?.companyId || !product) return;

        const companyId = userData.companyId;
        const unsubRecipes = onSnapshot(
            query(collection(db, 'companies', companyId, 'recipes')),
            snap => {
                const recipes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                const productRecipe = recipes.find(r =>
                    r.productId === productId ||
                    r.product === product.name
                );
                setRecipe(productRecipe || null);
            }
        );

        return () => unsubRecipes();
    }, [userData, product, productId]);

    // Helper function to get latest purchase price for a material
    const getMaterialPurchaseInfo = (materialId, materialName) => {
        // Find all purchases containing this material
        const purchasesWithMaterial = rawMaterialPurchases
            .filter(p => p.lineItems && p.lineItems.some(item =>
                item.productId === materialId || item.name === materialName || item.productName === materialName
            ))
            .sort((a, b) => {
                const dateA = a.purchaseDate?.toDate?.() || (a.purchaseDate?.seconds ? new Date(a.purchaseDate.seconds * 1000) : new Date(0));
                const dateB = b.purchaseDate?.toDate?.() || (b.purchaseDate?.seconds ? new Date(b.purchaseDate.seconds * 1000) : new Date(0));
                return dateB - dateA; // Sort by date descending (most recent first)
            });

        if (purchasesWithMaterial.length > 0) {
            // Get the most recent purchase
            const latestPurchase = purchasesWithMaterial[0];
            const lineItem = latestPurchase.lineItems.find(item =>
                item.productId === materialId || item.name === materialName || item.productName === materialName
            );
            if (lineItem && lineItem.price > 0) {
                let dateStr = '';
                try {
                    const date = latestPurchase.purchaseDate?.toDate?.() ||
                        (latestPurchase.purchaseDate?.seconds ? new Date(latestPurchase.purchaseDate.seconds * 1000) : null);
                    if (date) {
                        dateStr = date.toLocaleDateString('tr-TR');
                    }
                } catch (e) { }

                return {
                    price: lineItem.price,
                    date: dateStr,
                    supplierName: latestPurchase.supplierName,
                    hasPurchasePrice: true
                };
            }
        }
        return { hasPurchasePrice: false };
    };

    // Calculate costs when we have all the data
    const calculatedCostData = useMemo(() => {
        if (!recipe || !recipe.stages) return null;

        // Combine all raw materials from both sources with purchase prices
        const allMaterials = [
            ...rawMaterials.map(rm => {
                const purchaseInfo = getMaterialPurchaseInfo(rm.id, rm.name);
                return {
                    name: rm.name,
                    unitPrice: purchaseInfo.hasPurchasePrice ? purchaseInfo.price : (rm.unitPrice || 0),
                    unit: rm.unit || 'Adet',
                    ...purchaseInfo
                };
            }),
            ...allProducts.filter(p => p.isRawMaterial).map(p => {
                const purchaseInfo = getMaterialPurchaseInfo(p.id, p.name);
                return {
                    name: p.name,
                    unitPrice: purchaseInfo.hasPurchasePrice ? purchaseInfo.price : (p.purchasePrice || p.salePrice || 0),
                    unit: p.unit || 'Adet',
                    ...purchaseInfo
                };
            })
        ];

        const laborSettings = {
            hourlyRate: 15.00
        };

        const overheadSettings = {
            type: 'percentage',
            rate: 10
        };

        try {
            return calculateDetailedCost(
                recipe,
                allMaterials,
                subcontractors,
                laborSettings,
                overheadSettings
            );
        } catch (error) {
            console.error('Cost calculation error:', error);
            return null;
        }
    }, [recipe, rawMaterials, allProducts, subcontractors, rawMaterialPurchases]);

    useEffect(() => {
        setCostData(calculatedCostData);
    }, [calculatedCostData]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY'
        }).format(amount || 0);
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <div>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                        <p>Maliyet detayları yükleniyor...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <Link to="/uretim/maliyet-hesaplama" style={styles.backLink}>
                        ← Maliyet Hesaplama
                    </Link>
                </div>
                <div style={styles.noRecipeContainer}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
                    <h2>Ürün Bulunamadı</h2>
                    <p>Belirtilen ID ile ürün bulunamadı.</p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <Link to="/uretim/maliyet-hesaplama" style={styles.backLink}>
                    ← Maliyet Hesaplama
                </Link>
            </div>

            <div style={styles.box}>
                <h2 style={styles.title}>{product.name} - Detaylı Maliyet Analizi</h2>
                <p style={{ color: '#64748b', marginTop: '8px' }}>
                    {recipe ? `Reçete: ${recipe.name} | Versiyon: ${recipe.version || 'v1.0'}` : 'Reçete tanımlanmamış'}
                    {product.barcode && ` | Kod: ${product.barcode}`}
                </p>

                {!recipe ? (
                    <div style={styles.noRecipeContainer}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                        <h3 style={{ color: '#374151', marginBottom: '8px' }}>Bu ürün için reçete tanımlanmamış</h3>
                        <p style={{ marginBottom: '20px' }}>Maliyet hesaplaması yapabilmek için önce bir reçete oluşturmalısınız.</p>
                        <Link
                            to="/uretim/reçeteler"
                            style={{
                                padding: '12px 24px',
                                background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
                                color: 'white',
                                borderRadius: '12px',
                                textDecoration: 'none',
                                fontWeight: '600'
                            }}
                        >
                            Reçete Oluştur
                        </Link>
                    </div>
                ) : (
                    <>
                        {/* Recipe Stages Detail */}
                        <div style={{ marginTop: '30px' }}>
                            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#0f172a' }}>
                                📋 Reçete Aşamaları ({recipe.stages?.length || 0} aşama)
                            </h3>

                            {recipe.stages?.map((stage, index) => (
                                <div key={stage.id || index} style={styles.stageContainer}>
                                    <div style={styles.stageHeader}>
                                        <h4 style={styles.stageTitle}>
                                            {index + 1}. {stage.name}
                                        </h4>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <span style={{
                                                ...styles.stageBadge,
                                                background: stage.type === 'subcontracting' ? '#dbeafe' : '#dcfce7',
                                                color: stage.type === 'subcontracting' ? '#1e40af' : '#166534'
                                            }}>
                                                {stage.type === 'subcontracting' ? '🏭 Fason' : '🏠 İç Üretim'}
                                            </span>
                                            {stage.duration > 0 && (
                                                <span style={{
                                                    ...styles.stageBadge,
                                                    background: '#fef3c7',
                                                    color: '#92400e'
                                                }}>
                                                    ⏱️ {stage.duration} dk
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {stage.description && (
                                        <p style={{ color: '#64748b', marginBottom: '15px', fontSize: '14px' }}>
                                            {stage.description}
                                        </p>
                                    )}

                                    {/* Input Materials */}
                                    {stage.inputMaterials && stage.inputMaterials.length > 0 && (
                                        <div style={{ marginBottom: '15px' }}>
                                            <h5 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '10px' }}>
                                                📥 Giriş Malzemeleri:
                                            </h5>
                                            {stage.inputMaterials.map((material, mIndex) => {
                                                // Find the raw material to get unit price - CHECK PURCHASE PRICE FIRST
                                                const rawMat = [...rawMaterials, ...allProducts.filter(p => p.isRawMaterial)]
                                                    .find(rm => rm.name === material.material);

                                                // Get purchase info
                                                const purchaseInfo = getMaterialPurchaseInfo(rawMat?.id, material.material);
                                                const unitPrice = purchaseInfo.hasPurchasePrice
                                                    ? purchaseInfo.price
                                                    : (rawMat?.unitPrice || rawMat?.purchasePrice || rawMat?.salePrice || 0);
                                                const totalCost = unitPrice * (material.quantity || 0);

                                                return (
                                                    <div key={mIndex} style={styles.materialItem}>
                                                        <div>
                                                            <strong>{material.material || 'Malzeme belirtilmemiş'}</strong>
                                                            <span style={{ color: '#64748b', marginLeft: '10px' }}>
                                                                {material.quantity} {material.unit}
                                                            </span>
                                                            {purchaseInfo.hasPurchasePrice && (
                                                                <span style={styles.purchaseInfoBadge}>
                                                                    Satın Alma Fiyatı
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ textAlign: 'right' }}>
                                                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                                Birim: {formatCurrency(unitPrice)}
                                                                {purchaseInfo.hasPurchasePrice && (
                                                                    <div style={{ fontSize: '10px', color: '#059669', marginTop: '2px' }}>
                                                                        {purchaseInfo.supplierName && `${purchaseInfo.supplierName}`}
                                                                        {purchaseInfo.date && ` - ${purchaseInfo.date}`}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div style={{ fontWeight: '600', color: '#0f172a' }}>
                                                                {formatCurrency(totalCost)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Subcontractors */}
                                    {stage.type === 'subcontracting' && stage.subcontractors && stage.subcontractors.length > 0 && (
                                        <div style={styles.subcontractorInfo}>
                                            <h5 style={{ fontSize: '14px', fontWeight: '600', color: '#1e40af', marginBottom: '10px' }}>
                                                👥 Fason Tedarikçiler:
                                            </h5>
                                            {stage.subcontractors.map((sub, sIndex) => {
                                                const supplier = subcontractors.find(s => s.id === sub.supplierId);
                                                return (
                                                    <div key={sIndex} style={{
                                                        padding: '8px 0',
                                                        borderBottom: sIndex < stage.subcontractors.length - 1 ? '1px solid #bfdbfe' : 'none'
                                                    }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <span><strong>{sub.supplierName || supplier?.name || 'Bilinmeyen'}</strong></span>
                                                            <span>İşçilik: {formatCurrency(sub.laborCost || 0)}</span>
                                                        </div>
                                                        {supplier?.costTariff && supplier.costTariff.length > 0 && (
                                                            <div style={{ fontSize: '12px', color: '#1e40af', marginTop: '4px' }}>
                                                                Tarife: {supplier.costTariff.map(t =>
                                                                    `${t.value} ${t.currency || '₺'}/${t.unit}`
                                                                ).join(', ')}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Output Product */}
                                    {stage.outputProduct && stage.outputProduct.name && (
                                        <div style={styles.outputProduct}>
                                            <h5 style={{ fontSize: '14px', fontWeight: '600', color: '#166534', marginBottom: '5px' }}>
                                                📤 Çıktı Ürün:
                                            </h5>
                                            <span>
                                                <strong>{stage.outputProduct.name}</strong>
                                                {' - '}
                                                {stage.outputProduct.quantity} {stage.outputProduct.unit}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Cost Breakdown */}
                        {costData && (
                            <CostBreakdown
                                costData={costData}
                                productName={product.name}
                                quantity={product.quantity || 1}
                            />
                        )}

                        {/* Sale Price and Profit Margin */}
                        <div style={{
                            background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
                            borderRadius: '12px',
                            padding: '25px',
                            marginTop: '30px',
                            color: 'white'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Satış Fiyatı ve Kâr Marjı</h3>
                                    <p style={{ margin: '5px 0 0 0', opacity: '0.9' }}>
                                        Birim satış fiyatı: {formatCurrency(product.salePrice || 0)}
                                    </p>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '32px', fontWeight: '700' }}>
                                        {formatCurrency(product.salePrice || 0)}
                                    </div>
                                    {costData && costData.totals && (
                                        <div style={{ fontSize: '16px', marginTop: '5px' }}>
                                            Birim Maliyet: {formatCurrency(costData.totals.total)} |
                                            Kâr Marjı: {(((product.salePrice || 0) - costData.totals.total) / (product.salePrice || 1) * 100).toFixed(2)}%
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default CostCalculationDetail;